import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Toggle from '../components/ui/Toggle';
import Icon from '../components/ui/Icon';
import { APP_CONFIG } from '../data/constants.jsx';
import apiService from '../services/api';
import VerifyNumberModal from '../components/VerifyNumberModal';

// Curated dial codes for the phone field (BD-first). Picking from a dropdown
// keeps the stored phone consistent instead of free-typed +880 / 880+ / spaces.
const PHONE_COUNTRIES = [
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+92', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar' },
  { code: '+965', flag: '🇰🇼', name: 'Kuwait' },
  { code: '+968', flag: '🇴🇲', name: 'Oman' },
  { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+1', flag: '🇺🇸', name: 'United States' },
];
import AuthService from '../services/auth';

const CompleteProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const hasCheckedProfile = useRef(false); // Prevent multiple executions
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [dialCode, setDialCode] = useState('+880');
  const [promptVerify, setPromptVerify] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    confirmPassword: '',
    gender: '',
    country: 'Bangladesh',
    short_address: '',
    is_available: true,
    use_personal_call_times: false,
    personal_fajr_start_time: '',
    personal_fajr_end_time: '',
  });

  const countries = [
    { value: 'Bangladesh', label: t('countries.bangladesh') || 'বাংলাদেশ' },
    { value: 'Pakistan', label: t('countries.pakistan') || 'পাকিস্তান' },
    { value: 'India', label: t('countries.india') || 'ভারত' },
    { value: 'Saudi Arabia', label: t('countries.saudiArabia') || 'সৌদি আরব' },
    { value: 'UAE', label: t('countries.uae') || 'সংযুক্ত আরব আমিরাত' },
    { value: 'Qatar', label: t('countries.qatar') || 'কাতার' },
    { value: 'Kuwait', label: t('countries.kuwait') || 'কুয়েত' },
    { value: 'Bahrain', label: t('countries.bahrain') || 'বাহরাইন' },
    { value: 'Oman', label: t('countries.oman') || 'ওমান' },
    { value: 'Malaysia', label: t('countries.malaysia') || 'মালয়েশিয়া' },
    { value: 'Indonesia', label: t('countries.indonesia') || 'ইন্দোনেশিয়া' },
    { value: 'Turkey', label: t('countries.turkey') || 'তুরস্ক' },
    { value: 'Egypt', label: t('countries.egypt') || 'মিশর' },
    { value: 'Jordan', label: t('countries.jordan') || 'জর্ডান' },
    { value: 'Morocco', label: t('countries.morocco') || 'মরক্কো' },
    { value: 'Iraq', label: t('countries.iraq') || 'ইরাক' },
    { value: 'Syria', label: t('countries.syria') || 'সিরিয়া' },
    { value: 'Iran', label: t('countries.iran') || 'ইরান' },
    { value: 'Afghanistan', label: t('countries.afghanistan') || 'আফগানিস্তান' },
    { value: 'USA', label: t('countries.usa') || 'যুক্তরাষ্ট্র' },
    { value: 'UK', label: t('countries.uk') || 'যুক্তরাজ্য' },
    { value: 'Canada', label: t('countries.canada') || 'কানাডা' },
    { value: 'Australia', label: t('countries.australia') || 'অস্ট্রেলিয়া' },
    { value: 'Other', label: t('countries.other') || 'অন্যান্য' }
  ];

  useEffect(() => {
    // Prevent multiple executions (especially in React Strict Mode)
    if (hasCheckedProfile.current) {
      console.log('⏭️ CompleteProfile: Already initialized, skipping');
      return;
    }

    const initializeProfile = () => {
      console.log('🔄 CompleteProfile: Initializing...');

      // Get storage keys
      const tokenKey = import.meta.env.VITE_ACCESS_TOKEN_KEY || 'token';
      const refreshTokenKey = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refreshToken';
      const userKey = import.meta.env.VITE_USER_DATA_KEY || 'user';

      // Try to get tokens from localStorage first (normal flow via GoogleAuthSuccess)
      let accessToken = localStorage.getItem(tokenKey);
      let refreshToken = localStorage.getItem(refreshTokenKey);

      // Fallback: Check if tokens are in URL query params (shouldn't happen normally, but just in case)
      if (!accessToken || !refreshToken) {
        const urlParams = new URLSearchParams(window.location.search);
        accessToken = urlParams.get('access_token');
        refreshToken = urlParams.get('refresh_token');

        if (accessToken && refreshToken) {
          console.log('⚠️ CompleteProfile: Tokens found in URL (not in localStorage). This shouldn\'t happen normally.');
          // Store them properly
          localStorage.setItem(tokenKey, accessToken);
          localStorage.setItem(refreshTokenKey, refreshToken);
        }
      }

      if (!accessToken || !refreshToken) {
        console.error('❌ CompleteProfile: Missing tokens');
        setError('Invalid authentication. Please try again.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Ensure apiService has the token
      apiService.setToken(accessToken);
      console.log('🔑 CompleteProfile: API service token set');

      // Mark as checked
      hasCheckedProfile.current = true;

      // Get user info from localStorage (already fetched by GoogleAuthSuccess)
      try {
        const userStr = localStorage.getItem(userKey);
        if (userStr) {
          const user = JSON.parse(userStr);
          console.log('👤 CompleteProfile: User data loaded from localStorage');

          // Create userInfo object for the form
          setUserInfo({
            profile_completed: user.profile_completed || false,
            is_google_user: user.is_google_user || false,
            has_phone: !!user.phone_number,
            has_gender: !!user.gender,
            has_country: !!user.country,
            email: user.email || '',
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            profile_picture: user.profile_picture || null
          });

          // Pre-fill name from Google if available
          if (user.first_name || user.last_name) {
            setFormData(prev => ({
              ...prev,
              first_name: user.first_name || '',
              last_name: user.last_name || ''
            }));
          }

          // If profile already complete (shouldn't happen, but just in case)
          if (user.profile_completed) {
            console.log('✅ CompleteProfile: Profile already complete, redirecting to /profile');
            navigate('/profile', { replace: true });
          } else {
            console.log('📝 CompleteProfile: Profile incomplete, showing form');
          }
        } else {
          console.log('⚠️ CompleteProfile: No user data in localStorage, showing form anyway');
          setUserInfo({ profile_completed: false, is_google_user: true });
        }
      } catch (err) {
        console.error('❌ CompleteProfile: Error parsing user data:', err);
        // Show form anyway
        setUserInfo({ profile_completed: false, is_google_user: true });
      }
    };

    initializeProfile();
  }, [navigate]);

  const goToProfile = () => navigate('/profile', { replace: true, state: { showFajrReminder: true } });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // The dial code follows the country: choosing Bangladesh brings +880 back
    // by itself, so nobody has to know to change the code separately.
    if (e.target.name === 'country') {
      const match = PHONE_COUNTRIES.find((c) => c.name === e.target.value);
      if (match) setDialCode(match.code);
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.phone || !formData.password || !formData.gender || !formData.first_name) {
      setError(t('auth.completeProfile.allFieldsRequired') || 'নাম, ফোন নাম্বার, পাসওয়ার্ড এবং লিঙ্গ পূরণ করুন');
      setLoading(false);
      return;
    }

    // Combine the chosen country code with the local number (strip a leading 0
    // so +880 + 01712345678 doesn't become +88001712345678).
    const localDigits = formData.phone.replace(/\D/g, '').replace(/^0+/, '');
    if (localDigits.length < 8) {
      setError('সঠিক ফোন নম্বর দিন — দেশের কোড নির্বাচন করে বাকি নম্বরটি সম্পূর্ণ লিখুন।');
      setLoading(false);
      return;
    }
    const fullPhone = `${dialCode}${localDigits}`;

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.completeProfile.passwordMismatch') || 'পাসওয়ার্ড মিলছে না');
      setLoading(false);
      return;
    }

    if (formData.password.length < 4) {
      setError(t('auth.completeProfile.passwordTooShort') || 'পাসওয়ার্ড কমপক্ষে ৪ অক্ষরের হতে হবে');
      setLoading(false);
      return;
    }

    try {
      // Ensure token is set before making the request
      const tokenKey = import.meta.env.VITE_ACCESS_TOKEN_KEY || 'token';
      const refreshTokenKey = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refreshToken';
      const userKey = import.meta.env.VITE_USER_DATA_KEY || 'user';

      const accessToken = localStorage.getItem(tokenKey);
      if (!accessToken) {
        setError('Authentication expired. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
        setLoading(false);
        return;
      }

      // Set token in apiService (in case it was lost)
      apiService.setToken(accessToken);
      console.log('🔑 CompleteProfile: Token set before submitting form');

      const response = await apiService.request('/auth/complete-profile', {
        method: 'POST',
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone_number: fullPhone,
          password: formData.password,
          gender: formData.gender,
          country: formData.country,
          short_address: formData.short_address,
          is_available: formData.is_available,
          use_personal_call_times: formData.use_personal_call_times,
          personal_fajr_start_time: formData.personal_fajr_start_time || null,
          personal_fajr_end_time: formData.personal_fajr_end_time || null,
        }),
      });

      if (response.user) {
        // Store NEW tokens if returned (backend now returns fresh tokens)
        if (response.access_token && response.refresh_token) {
          // Use AuthService.setAuth() to properly sync everything
          AuthService.setAuth(response.access_token, response.refresh_token, response.user);
          console.log('✅ CompleteProfile: New tokens and user data synced via AuthService');
        } else {
          // Fallback: Just update user data if no new tokens
          localStorage.setItem(userKey, JSON.stringify(response.user));
          console.log('✅ CompleteProfile: User data updated (no new tokens)');
        }

        // Refresh auth context with updated user data
        await refreshUser();
        console.log('✅ CompleteProfile: AuthContext refreshed');

        // Success. An unverified number is invisible to everyone, so ask to
        // verify it now, before the profile page; "later" continues as before.
        if (response.user.otp_verified === true) goToProfile();
        else setPromptVerify(true);
      }
    } catch (err) {
      console.error('Failed to complete profile:', err);
      setError(err.message || t('auth.completeProfile.errorGeneric') || 'প্রোফাইল সম্পূর্ণ করতে ব্যর্থ');
    } finally {
      setLoading(false);
    }
  };

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Header with Google User Info */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-3 mb-6">
              {userInfo.profile_picture && (
                <img
                  src={userInfo.profile_picture}
                  alt="Profile"
                  className="w-16 h-16 rounded-full border-2 border-green-500"
                />
              )}
              <div className="text-left">
                <h1 className="font-bold text-2xl text-gray-800">
                  {t('auth.completeProfile.welcome') || 'স্বাগতম'}, {userInfo.first_name}!
                </h1>
                <p className="text-sm text-gray-600">{userInfo.email}</p>
              </div>
            </div>
            <p className="text-gray-600">
              {t('auth.completeProfile.subtitle') || 'আপনার প্রোফাইল সম্পূর্ণ করুন'}
            </p>
          </div>

          <Card className="p-8">
            <h2 className="text-xl font-semibold mb-4 text-center">
              {t('auth.completeProfile.title') || 'প্রোফাইল সম্পূর্ণ করুন'}
            </h2>

            <p className="text-sm text-gray-600 mb-6 text-center">
              {t('auth.completeProfile.description') || 'সব সেবা পেতে নিচের তথ্যগুলো দিন'}
            </p>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Availability Setting - FIRST */}
              <div className="pb-2">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Bell" className="w-5 h-5 text-indigo-600" />
                      <h4 className="text-base font-semibold text-gray-900">অন্য কেউ আপনাকে জাগাবে?</h4>
                    </div>
                    <p className="text-sm text-gray-600">আপনি না উঠলে আপনার নাম্বার অন্য মানুষের কাছে যাবে, যেনো আপনাকে তারা কল দিতে পারে।</p>
                  </div>
                  <Toggle
                    checked={formData.is_available}
                    onChange={(checked) => setFormData({ ...formData, is_available: checked })}
                    variant="success"
                    size="default"
                  />
                </div>
              </div>

              {/* Call Time Preferences - SECOND */}
              <div className="pb-2">
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="Clock" className="w-5 h-5 text-indigo-600" />
                        <h4 className="text-base font-semibold text-gray-900">কল টাইম পছন্দ</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">আপনি কখন কল রিসিভ করতে চান?</p>
                    </div>
                  </div>

                  {/* Use Personal Call Times Toggle */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="Settings" className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900 block text-sm">ব্যক্তিগত সময় ব্যবহার করুন</span>
                        <p className="text-xs text-gray-500">বন্ধ থাকলে সিস্টেম ডিফল্ট সময় ব্যবহার হবে</p>
                      </div>
                    </div>
                    <Toggle
                      checked={formData.use_personal_call_times}
                      onChange={(checked) => setFormData({ ...formData, use_personal_call_times: checked })}
                      variant="success"
                      size="default"
                    />
                  </div>

                  {/* Time Inputs (visible when use_personal_call_times is true) */}
                  {formData.use_personal_call_times && (
                    <div className="bg-white border border-gray-200 p-4 rounded-lg space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                            <Icon name="Sunrise" className="w-4 h-4 text-blue-600" />
                            <span>শুরুর সময়</span>
                          </label>
                          <input
                            type="time"
                            value={formData.personal_fajr_start_time}
                            onChange={(e) => setFormData({ ...formData, personal_fajr_start_time: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                            <Icon name="Sunset" className="w-4 h-4 text-green-600" />
                            <span>শেষ সময়</span>
                          </label>
                          <input
                            type="time"
                            value={formData.personal_fajr_end_time}
                            onChange={(e) => setFormData({ ...formData, personal_fajr_end_time: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        </div>
                      </div>
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-amber-800 text-xs">
                          <Icon name="AlertTriangle" className="w-4 h-4" />
                          <span><strong>বিঃদ্রঃ</strong> শুরু এবং শেষ সময়ের মধ্যে কমপক্ষে ৩০ মিনিট ব্যবধান রাখুন</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Name Fields - THIRD */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    নাম (First Name) *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="আপনার নাম"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    পদবী (Last Name)
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="আপনার পদবী (ঐচ্ছিক)"
                  />
                </div>
              </div>

              {/* Country - FOURTH */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.form.country')}
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {countries.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Short Address - FIFTH */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  সংক্ষিপ্ত ঠিকানা (Short Address)
                </label>
                <input
                  type="text"
                  name="short_address"
                  value={formData.short_address}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="শহর, জেলা বা এলাকার নাম (যেমন: ঢাকা, মিরপুর)"
                />
              </div>

              {/* Phone Number - SIXTH */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.form.phone')} *
                </label>
                <div className="flex gap-2">
                  <select
                    value={dialCode}
                    onChange={(e) => setDialCode(e.target.value)}
                    aria-label="Country code"
                    className="px-2 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {PHONE_COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    inputMode="numeric"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="1712345678"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  দেশের কোড নির্বাচন করে বাকি নম্বরটি লিখুন (যেমন: 1712345678)।
                </p>
              </div>

              {/* Gender - SEVENTH */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  লিঙ্গ *
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">{t('auth.form.genderPlaceholder')}</option>
                  <option value="M">{t('auth.form.male')}</option>
                  {/* Shown but not selectable: the service is for men for now. */}
                  <option value="F" disabled>{t('auth.form.female')}</option>
                </select>
              </div>

              {/* Password - EIGHTH with show/hide icon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.form.password')} *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength="4"
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder={t('auth.form.passwordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <Icon name={showPassword ? "EyeOff" : "Eye"} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Confirm Password - NINTH with show/hide icon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.completeProfile.confirmPassword') || 'পাসওয়ার্ড নিশ্চিত করুন'} *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength="4"
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder={t('auth.completeProfile.confirmPasswordPlaceholder') || 'পাসওয়ার্ড আবার লিখুন'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading
                  ? t('common.loading')
                  : t('auth.completeProfile.submit') || 'প্রোফাইল সম্পূর্ণ করুন'
                }
              </Button>
            </form>
          </Card>
        </div>
      </div>

      <Footer />
      <VerifyNumberModal isOpen={promptVerify} onClose={goToProfile} />
    </div>
  );
};

export default CompleteProfile;
